import { useEffect, type ReactNode } from 'react';
import { Icon } from './Icon';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** Centred modal with paper backdrop. Esc + backdrop-click to close. */
export function Modal({ open, onClose, title, children, footer, width = 460 }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="animate-fadeBg"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 150,
        background: 'rgba(28,27,25,.34)',
        backdropFilter: 'blur(2px)',
        display: 'grid',
        placeItems: 'center',
        padding: 18,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, border: 'none', background: 'transparent', cursor: 'default' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="card animate-modalIn"
        style={{ position: 'relative', width, maxWidth: '94vw', maxHeight: '88vh', overflow: 'auto', boxShadow: 'var(--sh-lg)' }}
      >
        {title && (
          <div className="row between hairline" style={{ padding: '15px 18px' }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="btn-quiet"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 8 }}
            >
              <Icon name="x" size={18} />
            </button>
          </div>
        )}
        <div style={{ padding: 18 }}>{children}</div>
        {footer && (
          <div
            className="row"
            style={{ padding: '13px 18px', borderTop: '1px solid var(--line)', justifyContent: 'flex-end', gap: 9 }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
